/* eslint-disable no-undef */
import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, Table } from 'semantic-ui-react';
import { toast, ToastContainer } from 'react-toastify';

import Heading from '../../components/Heading';
import DragAndDrop from '../../components/DragAndDrop';
import Pagination from '../../components/Pagination';
import CountDisplay from '../../components/CountDisplay';
import ButtonContainer from '../../components/ButtonContainer';
import Dropdown from '../../components/Dropdown/Dropdown';
import Loader from '../../components/Loader';
import Button from '../../components/Button';

import user from '../../store/user';
import { adminFeatureMapping } from '../../models/adminFeatureMapping';
import fetchNTCPremiumJobs from '../../services/fetchNTCPremiumJobs';
import fetchNTCPremiumJobAssignees from '../../services/fetchNTCPremiumJobAssignees';
import uploadNTCPremiumJobsCsv from '../../services/uploadNTCPremiumJobsCsv';
import updateNTCPremiumJob from '../../services/updateNTCPremiumJob';

import './styles.scss';

const PAGE_CONTENT = 20;
const CURRENT_PLATFORM = process.env.REACT_APP_PLATFORM;

const DEFAULT_BRANCH_OPTION = {
    id: '',
    name: 'All Branches'
};

const DEFAULT_ASSIGNEE_OPTION = {
    id: '',
    name: 'All Assignees'
};

const DEFAULT_PERMISSIONS = {
    role: '',
    showAssignedToFilter: false,
    canAssign: false
};

const getErrorMessage = (error, fallback) => {
    if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
    }

    if (error && error.data && error.data.message) {
        return error.data.message;
    }

    if (error && error.message) {
        return error.message;
    }

    return fallback;
};

const formatCountRange = (pageNumber, listLength, totalCount) => {
    if (!totalCount) {
        return { start: 0, end: 0 };
    }

    const start = ((pageNumber - 1) * PAGE_CONTENT) + 1;
    const end = start + listLength - 1;
    return { start, end };
};

const renderLabelValue = (label, value) => (
    <div>
        <strong>{label}</strong>: {value || 'N/A'}
    </div>
);

const NTCPremiumJobs = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [adminId, setAdminId] = useState(null);
    const [hasAccess, setHasAccess] = useState(true);
    const [isManager, setIsManager] = useState(false);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [branch, setBranch] = useState('');
    const [branches, setBranches] = useState([]);
    const [jobId, setJobId] = useState('');
    const [comment, setComment] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [assignees, setAssignees] = useState([]);
    const [commentInputs, setCommentInputs] = useState({});
    const [rowUpdateMap, setRowUpdateMap] = useState({});
    const [checkedIds, setCheckedIds] = useState([]);
    const [bulkAssignTo, setBulkAssignTo] = useState('');

    const branchOptions = useMemo(() => {
        return [
            DEFAULT_BRANCH_OPTION,
            ...branches.map(item => ({ id: item, name: item }))
        ];
    }, [branches]);

    const assigneeOptions = useMemo(() => {
        return [
            DEFAULT_ASSIGNEE_OPTION,
            ...assignees.map(item => ({ id: item.id, name: item.name }))
        ];
    }, [assignees]);

    const assigneeNameMap = useMemo(() => {
        return assignees.reduce((acc, item) => {
            acc[item.id] = item.name;
            return acc;
        }, {});
    }, [assignees]);

    // Only ntcPremiumJobId and refrenceId are passed — no field prefill
    const getPostJobRedirectUrl = (row) => {
        const params = [
            ['ntcPremiumJobId', row.id],
            ['returnTo', '/ntc-premium-jobs'],
            ['refrenceId', row.jobId],
        ].filter(([, value]) => value !== null && value !== undefined && value !== '');

        if (!params.length) {
            return '/postjob';
        }

        return `/postjob?${params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&')}`;
    };

    const fetchList = async (
        nextPageNumber = 1,
        nextBranch = branch,
        nextJobId = jobId,
        nextAssignedTo = assignedTo,
        nextComment = comment
    ) => {
        if (!adminId) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetchNTCPremiumJobs(adminId, {
                pageNumber: nextPageNumber,
                pageContent: PAGE_CONTENT,
                branch: nextBranch,
                jobId: nextJobId,
                // assignees always see only their own jobs
                assignedTo: isManager ? nextAssignedTo : adminId,
                comment: nextComment
            });
            if (!response || response.status !== 'success') {
                throw response;
            }
            const data = response.data || {};
            const list = data.list || [];
            setRows(list);
            setTotalCount(data.count || 0);
            setBranches(data.branches || []);
            setPermissions(data.permissions || DEFAULT_PERMISSIONS);
            setCommentInputs(list.reduce((acc, item) => {
                acc[item.id] = item.comment || '';
                return acc;
            }, {}));
            setPageNumber(nextPageNumber);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch NTC premium jobs'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            const profile = await user.getProfile();
            setAdminId(profile.id);
            const managerAccess = user.checkFeatureAccess(adminFeatureMapping.NTC_PREMIUM_JOBS_MANAGER);
            const assigneeAccess = user.checkFeatureAccess(adminFeatureMapping.NTC_PREMIUM_JOBS_ASSIGNEE);
            setHasAccess(managerAccess || assigneeAccess);
            setIsManager(managerAccess);
        })();
    }, []);

    useEffect(() => {
        if (adminId && hasAccess) {
            fetchList(1, branch, jobId, assignedTo, comment);
        } else if (adminId && !hasAccess) {
            setLoading(false);
        }
    }, [adminId, hasAccess]);

    useEffect(() => {
        if (!(adminId && isManager)) {
            return;
        }

        (async () => {
            const response = await fetchNTCPremiumJobAssignees(adminId);
            if (response && response.status === 'success') {
                setAssignees(response.data || []);
            }
        })();
    }, [adminId, isManager]);

    const handleUpload = async (file) => {
        if (!adminId) {
            toast.error('Admin ID is not available');
            return;
        }

        const formData = new FormData();
        formData.append('csv', file);

        setUploading(true);
        try {
            const response = await uploadNTCPremiumJobsCsv(adminId, formData);
            if (response && response.status === 'success') {
                const data = response.data || {};
                const insertedCount = data.insertedCount || 0;
                const updatedCount = data.updatedCount || 0;
                toast.success(`Uploaded successfully. Jobs: inserted ${insertedCount}, updated ${updatedCount}.`);
                setSelectedFile(null);
                setUploadModalOpen(false);
                await fetchList(1, branch, jobId, assignedTo, comment);
                return;
            }
            throw response;
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to upload NTC premium jobs CSV'));
            setSelectedFile(null);
        } finally {
            setUploading(false);
        }
    };

    const setRowUpdating = (key, value) => {
        setRowUpdateMap(prev => ({ ...prev, [key]: value }));
    };

    const handlePageChange = (nextPageNumber) => {
        setCheckedIds([]);
        fetchList(nextPageNumber, branch, jobId, assignedTo, comment);
    };

    const handleCheckboxChange = (rowId) => {
        setCheckedIds(prev =>
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
        );
    };

    const handleSelectAll = () => {
        setCheckedIds(prev => prev.length === rows.length ? [] : rows.map(r => r.id));
    };

    const handleBulkAssign = async (assignTo) => {
        if (!assignTo || !checkedIds.length) {
            return;
        }
        try {
            await Promise.all(
                checkedIds.map(rowId => updateNTCPremiumJob(adminId, rowId, { assignedTo: assignTo }))
            );
            setRows(prev => prev.map(row =>
                checkedIds.includes(row.id)
                    ? { ...row, assignedTo: Number(assignTo), assignedToName: assigneeNameMap[assignTo] || '' }
                    : row
            ));
            setCheckedIds([]);
            setBulkAssignTo('');
            toast.success('Assigned successfully');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to assign jobs'));
        }
    };

    const handleBranchChange = (event) => {
        const nextBranch = event.target.value;
        setBranch(nextBranch);
        fetchList(1, nextBranch, jobId, assignedTo, comment);
    };

    const handleAssignedToFilterChange = (event) => {
        const nextAssignedTo = event.target.value;
        setAssignedTo(nextAssignedTo);
        fetchList(1, branch, jobId, nextAssignedTo, comment);
    };

    const handleJobIdChange = (event) => {
        setJobId(event.target.value);
    };

    const handleJobIdSearch = (event) => {
        if (event.key === 'Enter') {
            fetchList(1, branch, jobId, assignedTo, comment);
        }
    };

    const handleCommentFilterChange = (event) => {
        setComment(event.target.value);
    };

    const handleCommentFilterSearch = (event) => {
        if (event.key === 'Enter') {
            fetchList(1, branch, jobId, assignedTo, comment);
        }
    };

    const handlePostJob = (row) => {
        window.location.href = getPostJobRedirectUrl(row);
    };

    const handleRowCommentChange = (rowId, value) => {
        setCommentInputs(prev => ({ ...prev, [rowId]: value }));
    };

    const handleRowCommentSave = async (rowId) => {
        const updateKey = `${rowId}-comment`;
        setRowUpdating(updateKey, true);
        try {
            const response = await updateNTCPremiumJob(adminId, rowId, {
                comment: commentInputs[rowId] || ''
            });
            if (!response || response.status !== 'success') {
                throw response;
            }
            setRows(prev => prev.map(row => row.id === rowId ? {
                ...row,
                comment: commentInputs[rowId] || ''
            } : row));
            toast.success('Comment updated successfully');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update comment'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const handleRowAssigneeChange = async (rowId, value) => {
        const updateKey = `${rowId}-assignee`;
        setRowUpdating(updateKey, true);
        try {
            const response = await updateNTCPremiumJob(adminId, rowId, {
                assignedTo: value || null
            });
            if (!response || response.status !== 'success') {
                throw response;
            }
            setRows(prev => prev.map(row => row.id === rowId ? {
                ...row,
                assignedTo: value ? Number(value) : null,
                assignedToName: value ? assigneeNameMap[value] || '' : ''
            } : row));
            toast.success('Assignee updated successfully');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update assignee'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const onError = (message) => {
        toast.error(message);
    };

    const countRange = formatCountRange(pageNumber, rows.length, totalCount);

    if (!hasAccess && adminId) {
        return <div className="ntc-premium-jobs__restricted">You do not have access to this page.</div>;
    }

    return (
        <div className="ntc-premium-jobs">
            <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnVisibilityChange
                draggable={false}
                pauseOnHover
            />

            <div className="page-heading ntc-premium-jobs__page-heading">
                <Heading text="NTC Premium Jobs" />
                {isManager && (
                    <Button skin="dark" text="Upload CSV" clickButton={() => setUploadModalOpen(true)} />
                )}
            </div>

            {/* Upload CSV modal — manager only */}
            <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} size="small">
                <Modal.Header>Upload CSV</Modal.Header>
                <Modal.Content>
                    <p>
                        Upload the latest CSV for <strong>{CURRENT_PLATFORM}</strong>.
                        Only jobs matching this platform are stored.
                    </p>
                    <DragAndDrop
                        formats={['csv', 'xlsx']}
                        onError={onError}
                        onUpload={handleUpload}
                        selectedFile={selectedFile}
                        setSelectedFile={setSelectedFile}
                    />
                    {uploading && <div className="ntc-premium-jobs__upload-status">Uploading CSV...</div>}
                </Modal.Content>
                <Modal.Actions>
                    <Button skin="light" text="Cancel" clickButton={() => setUploadModalOpen(false)} />
                </Modal.Actions>
            </Modal>

            <ButtonContainer heading="Filters">
                <li>
                    <Dropdown data={branchOptions} defaultValue={branch} dropdownChange={handleBranchChange} />
                </li>
                <li>
                    <input
                        className="ntc-premium-jobs__search-input"
                        type="text"
                        placeholder="Search by Job ID"
                        value={jobId}
                        onChange={handleJobIdChange}
                        onKeyDown={handleJobIdSearch}
                    />
                </li>
                {isManager ? (
                    <li>
                        <Dropdown data={assigneeOptions} defaultValue={assignedTo} dropdownChange={handleAssignedToFilterChange} />
                    </li>
                ) : null}
                <li>
                    <input
                        className="ntc-premium-jobs__search-input"
                        type="text"
                        placeholder="Search by Comment"
                        value={comment}
                        onChange={handleCommentFilterChange}
                        onKeyDown={handleCommentFilterSearch}
                    />
                </li>
                <li>
                    <Button skin="dark" text="Search" clickButton={() => fetchList(1, branch, jobId, assignedTo, comment)} />
                </li>
                <li>
                    <Button skin="dark" text="Refresh" clickButton={() => fetchList(pageNumber, branch, jobId, assignedTo, comment)} />
                </li>
            </ButtonContainer>

            <div className="row text-center pagination-bottom ntc-premium-jobs__table-actions">
                <Pagination
                    totalItemsCount={totalCount}
                    content={PAGE_CONTENT}
                    pageNumber={pageNumber}
                    handlePageChange={handlePageChange}
                />
                <CountDisplay start={countRange.start} end={countRange.end} total={totalCount} />
            </div>

            {/* Bulk actions — manager only */}
            {isManager && (
                <div className="ntc-premium-jobs__bulk-actions">
                    <Checkbox
                        checked={rows.length > 0 && checkedIds.length === rows.length}
                        indeterminate={checkedIds.length > 0 && checkedIds.length < rows.length}
                        onChange={handleSelectAll}
                        label="Select All"
                    />
                    {checkedIds.length > 0 && (
                        <select
                            className="ntc-premium-jobs__bulk-assign-select"
                            value={bulkAssignTo}
                            onChange={(e) => {
                                const val = e.target.value;
                                setBulkAssignTo(val);
                                if (val) handleBulkAssign(val);
                            }}
                        >
                            <option value="">Assign to…</option>
                            {assignees.map(item => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {loading ? (
                <Loader />
            ) : (
                <Table fixed selectable className="ntc-premium-jobs__table">
                    <Table.Header>
                        <Table.Row>
                            {isManager && <Table.HeaderCell width={1} />}
                            <Table.HeaderCell>Job Id</Table.HeaderCell>
                            <Table.HeaderCell>Title</Table.HeaderCell>
                            <Table.HeaderCell>Recruiter Name / Email</Table.HeaderCell>
                            <Table.HeaderCell>Details</Table.HeaderCell>
                            <Table.HeaderCell>Views / Applied / Shortlisted</Table.HeaderCell>
                            <Table.HeaderCell>Post Date / Premium Details</Table.HeaderCell>
                            <Table.HeaderCell>Company Details / Refresh Status</Table.HeaderCell>
                            <Table.HeaderCell>Assigned To</Table.HeaderCell>
                            <Table.HeaderCell>Comment</Table.HeaderCell>
                            <Table.HeaderCell>URL</Table.HeaderCell>
                            <Table.HeaderCell>Post Job</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.length ? rows.map(row => (
                            <Table.Row key={row.id}>
                                {isManager && (
                                    <Table.Cell width={1}>
                                        <Checkbox
                                            checked={checkedIds.includes(row.id)}
                                            onChange={() => handleCheckboxChange(row.id)}
                                        />
                                    </Table.Cell>
                                )}
                                <Table.Cell>
                                    <div>{row.jobId}</div>
                                    {renderLabelValue('Platform', row.platform || row.relevantPlatform)}
                                    {renderLabelValue('Branch', row.branch)}
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="ntc-premium-jobs__title">{row.title || 'N/A'}</div>
                                    {renderLabelValue('Cust ID', row.custId)}
                                    {renderLabelValue('Priority', row.priority)}
                                </Table.Cell>
                                <Table.Cell>
                                    <div>{row.recruiterName || ''}</div>
                                    <div>{row.recruiterEmail || ''}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    {renderLabelValue('Category', 'IT / Software')}
                                    {renderLabelValue('Functional Area', 'Engineering')}
                                    {renderLabelValue('CTC', row.ctc)}
                                    {renderLabelValue('Experience', row.experience)}
                                    {renderLabelValue('Location', row.jobLocation)}
                                    {renderLabelValue('Relevant Applies', row.relevantAppliesOnNaukri)}
                                </Table.Cell>
                                <Table.Cell>
                                    <div>0 / 0 / 0</div>
                                </Table.Cell>
                                <Table.Cell>
                                    {renderLabelValue('Post Date', row.jobPostingDate)}
                                    {renderLabelValue('Last Premium On', 'N/A')}
                                    {renderLabelValue('No Of Times Premium', '0')}
                                </Table.Cell>
                                <Table.Cell>
                                    {renderLabelValue('Company Name', row.companyName)}
                                    {renderLabelValue('Type', row.billingCustomerFlag || 'Unknown')}
                                    {renderLabelValue('Branded', 'No')}
                                    {renderLabelValue('Refreshed', 'No')}
                                    {renderLabelValue('NTC Job Status', row.ntcJobStatus)}
                                </Table.Cell>
                                <Table.Cell>
                                    {/* managers can reassign; assignees see their name only */}
                                    {isManager ? (
                                        <Dropdown
                                            data={[{ id: '', name: 'Unassigned' }, ...assignees.map(item => ({ id: item.id, name: item.name }))]}
                                            defaultValue={row.assignedTo || ''}
                                            dropdownChange={(event) => handleRowAssigneeChange(row.id, event.target.value)}
                                            className="ntc-premium-jobs__inline-dropdown"
                                        />
                                    ) : (
                                        <div>{row.assignedToName || assigneeNameMap[row.assignedTo] || 'Unassigned'}</div>
                                    )}
                                    {rowUpdateMap[`${row.id}-assignee`] ? <div className="ntc-premium-jobs__inline-status">Saving...</div> : null}
                                </Table.Cell>
                                <Table.Cell>
                                    <textarea
                                        className="ntc-premium-jobs__comment-input"
                                        rows="4"
                                        value={commentInputs[row.id] || ''}
                                        onChange={(event) => handleRowCommentChange(row.id, event.target.value)}
                                    />
                                    <Button
                                        skin="dark"
                                        text="Save Comment"
                                        clickButton={() => handleRowCommentSave(row.id)}
                                        loading={!!rowUpdateMap[`${row.id}-comment`]}
                                        disabled={!!rowUpdateMap[`${row.id}-comment`]}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    {row.jobLink ? (
                                        <a href={row.jobLink} target="_blank" rel="noreferrer">
                                            Job Link
                                        </a>
                                    ) : 'N/A'}
                                </Table.Cell>
                                <Table.Cell>
                                    {row.isPosted ? (
                                        <span className="ntc-premium-jobs__posted-badge">NTC Posted</span>
                                    ) : (
                                        <Button
                                            skin="dark"
                                            text="Post Job"
                                            clickButton={() => handlePostJob(row)}
                                        />
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        )) : (
                            <Table.Row>
                                <Table.Cell colSpan={isManager ? 12 : 11} className="ntc-premium-jobs__empty">
                                    No records found
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            )}

            <div className="row text-center pagination-bottom">
                <Pagination
                    totalItemsCount={totalCount}
                    content={PAGE_CONTENT}
                    pageNumber={pageNumber}
                    handlePageChange={handlePageChange}
                />
            </div>
        </div>
    );
};

export default NTCPremiumJobs;
